import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Sparkles, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import type { DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, patch, post } from '../../nucleo/api';
import { dinheiro, numero } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CamposLocais, ErroGeral, useEnvio } from './formulario-local';
import { COR_NATUREZA, INCIDENCIAS, NATUREZAS, invalidarFolha, type Evento } from './tipos';

function valoresIniciais(e: Evento | null): Record<string, unknown> {
  return e
    ? { codigo: e.codigo, nome: e.nome, natureza: e.natureza, tipo: e.tipo, forma: e.forma, valor_centavos: e.valor_centavos, percentual: e.percentual, incide_em: e.incide_em, ativo: e.ativo }
    : { natureza: 'provento', tipo: 'fixo', forma: 'valor', incide_em: [], ativo: true };
}

function FormEvento({ evento, aoSalvar }: { evento: Evento | null; aoSalvar: () => void }) {
  const { t } = useTranslation('folha');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [valores, setValores] = useState<Record<string, unknown>>(() => valoresIniciais(evento));
  const envio = useEnvio();
  const padrao = !!evento?.especial;
  // Evento padrão com cálculo próprio (INSS, IRRF e benefícios) não tem valor nem percentual fixos.
  const semValorFixo = ['inss', 'irrf', 'beneficios', 'beneficios_desconto'].includes(evento?.especial ?? '');
  const incide = (valores.incide_em as string[] | undefined) ?? [];

  const campos: DefCampo[] = [
    { nome: 'codigo', rotulo: t('campos.codigo'), tipo: 'texto', obrigatorio: true, largura: 'meia', desabilitado: padrao, ajuda: t('campos.codigoAjuda') },
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true, largura: 'meia' },
    { nome: 'natureza', rotulo: t('campos.natureza'), tipo: 'selecao', obrigatorio: true, largura: 'meia', desabilitado: padrao, opcoes: NATUREZAS.map((v) => ({ valor: v, rotulo: t(`natureza.${v}`) })) },
    { nome: 'tipo', rotulo: t('campos.tipoEvento'), tipo: 'selecao', obrigatorio: true, largura: 'meia', desabilitado: padrao, ajuda: t('campos.tipoEventoAjuda'), opcoes: [{ valor: 'fixo', rotulo: t('tipoEvento.fixo') }, { valor: 'variavel', rotulo: t('tipoEvento.variavel') }] },
    { nome: 'forma', rotulo: t('campos.formaEvento'), tipo: 'selecao', obrigatorio: true, largura: 'meia', desabilitado: semValorFixo, opcoes: [{ valor: 'valor', rotulo: t('forma.valor') }, { valor: 'percentual', rotulo: t('forma.percentualSalario') }] },
    { nome: 'valor_centavos', rotulo: t('campos.valor'), tipo: 'dinheiro', obrigatorio: !semValorFixo, largura: 'meia', visivel: (v) => v.forma === 'valor' && !semValorFixo },
    { nome: 'percentual', rotulo: t('campos.percentual'), tipo: 'percentual', obrigatorio: !semValorFixo, largura: 'meia', min: 0, max: 1000, passo: 0.01, visivel: (v) => v.forma === 'percentual' && !semValorFixo },
    { nome: 'ativo', rotulo: t('campos.ativo'), tipo: 'booleano', largura: 'meia' },
  ];

  async function salvar() {
    const v = valores;
    if (!envio.validar(campos, v)) return;
    const corpo = {
      ...(padrao ? {} : { codigo: v.codigo, natureza: v.natureza, tipo: v.tipo }),
      nome: v.nome,
      forma: v.forma,
      valor_centavos: v.forma === 'valor' && !semValorFixo ? (v.valor_centavos ?? null) : null,
      percentual: v.forma === 'percentual' && !semValorFixo ? (v.percentual ?? null) : null,
      incide_em: v.natureza === 'provento' ? incide : [],
      ativo: !!v.ativo,
    };
    const ok = await envio.enviar(async () => {
      if (evento) await patch(`/folha/eventos/${evento.id}`, corpo);
      else await post('/folha/eventos', corpo);
    });
    if (ok) {
      await invalidarFolha(qc);
      toast.sucesso(tn('comum.salvo'));
      aoSalvar();
    }
  }

  return (
    <form
      id="form-evento"
      noValidate
      className="grid grid-cols-1 gap-4 sm:grid-cols-6"
      onSubmit={(e) => {
        e.preventDefault();
        void salvar();
      }}
    >
      <ErroGeral texto={envio.geral} />
      {padrao && <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2 sm:col-span-6">{t('eventos.padraoAviso')}</p>}
      <CamposLocais campos={campos} valores={valores} erros={envio.erros} aoMudar={(n, x) => setValores((a) => ({ ...a, [n]: x }))} />
      {valores.natureza === 'provento' && (
        <fieldset className="sm:col-span-6">
          <legend className="mb-1.5 text-sm font-medium text-tinta-2">{t('campos.incideEm')}</legend>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {INCIDENCIAS.map((i) => (
              <label key={i} className="flex cursor-pointer items-center gap-2 text-sm text-tinta-1">
                <input type="checkbox" checked={incide.includes(i)} onChange={(e) => setValores((a) => ({ ...a, incide_em: e.target.checked ? [...incide, i] : incide.filter((x) => x !== i) }))} />
                {t(`incidencia.${i}`)}
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-tinta-3">{t('campos.incideEmAjuda')}</p>
        </fieldset>
      )}
    </form>
  );
}

/** M8.2: eventos de folha (proventos, descontos e encargos), fixos ou variáveis. */
// Val5 (29/10): eventos e benefícios são configuração da folha (`folha.configurar`).
export function Eventos() {
  const { t } = useTranslation('folha');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<Evento | 'novo' | null>(null);
  const [remover, setRemover] = useState<Evento | null>(null);
  const atual = aberto && aberto !== 'novo' ? aberto : null;

  const excluir = useMutation({
    mutationFn: (id: string) => del(`/folha/eventos/${id}`),
    onSuccess: async () => {
      setRemover(null);
      setAberto(null);
      await invalidarFolha(qc);
    },
    onError: (e) => {
      setRemover(null);
      toast.erro(mensagemDeErro(e));
    },
  });
  const padrao = useMutation({
    mutationFn: () => post<{ criados: number }>('/folha/eventos/padrao'),
    onSuccess: async (r) => {
      await invalidarFolha(qc);
      toast.sucesso(t('eventos.padraoCriados', { count: r.criados }));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const colunas: Coluna<Evento>[] = [
    { chave: 'codigo', rotulo: t('campos.codigo'), mono: true, render: (e) => <span className="font-medium">{e.codigo}</span> },
    { chave: 'nome', rotulo: t('campos.nome'), principal: true },
    { chave: 'natureza', rotulo: t('campos.natureza'), render: (e) => <Selo cor={COR_NATUREZA[e.natureza]}>{t(`natureza.${e.natureza}`)}</Selo> },
    { chave: 'tipo', rotulo: t('campos.tipoEvento'), render: (e) => t(`tipoEvento.${e.tipo}`) },
    {
      chave: 'valor',
      rotulo: t('campos.valor'),
      alinhar: 'direita',
      mono: true,
      render: (e) => (e.especial && e.valor_centavos === null && e.percentual === null ? t(`calculoProprio.${e.especial}`) : e.forma === 'valor' ? dinheiro(e.valor_centavos) : `${numero(e.percentual ?? 0, 2)}%`),
    },
    { chave: 'incide_em', rotulo: t('campos.incideEm'), render: (e) => (e.incide_em.length ? e.incide_em.map((i) => t(`incidencia.${i}`)).join(', ') : '-') },
    { chave: 'ativo', rotulo: t('campos.situacao'), render: (e) => <Selo cor={e.ativo ? 'verde' : 'neutro'}>{e.ativo ? t('situacao.ativo') : t('situacao.inativo')}</Selo> },
  ];

  return (
    <>
      <AcaoPrimaria>
        {s.pode('folha.configurar') && (
          <>
            <Botao icone={<Sparkles className="size-4" />} carregando={padrao.isPending} onClick={() => padrao.mutate()}>
              {t('acoes.criarPadrao')}
            </Botao>
            <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
              {t('acoes.novoEvento')}
            </Botao>
          </>
        )}
      </AcaoPrimaria>
      <Tabela<Evento>
        tela="folha.eventos"
        caminho="/folha/eventos"
        colunas={colunas}
        aoClicar={(e) => setAberto(e)}
        vazio={
          <Vazio
            texto={t('vazio.eventos')}
            acao={
              s.pode('folha.configurar') ? (
                <Botao icone={<Sparkles className="size-4" />} carregando={padrao.isPending} onClick={() => padrao.mutate()}>
                  {t('acoes.criarPadrao')}
                </Botao>
              ) : undefined
            }
          />
        }
      />
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        largura="lg"
        titulo={atual ? `${atual.codigo} ${atual.nome}` : t('acoes.novoEvento')}
        rodape={
          <>
            {atual && !atual.especial && s.pode('folha.configurar') && (
              <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setRemover(atual)}>
                {tn('comum.excluir')}
              </Botao>
            )}
            {s.pode('folha.configurar') && (
              <>
                <Botao variante="fantasma" onClick={() => setAberto(null)}>
                  {tn('comum.cancelar')}
                </Botao>
                <Botao variante="primario" type="submit" form="form-evento">
                  {tn('comum.salvar')}
                </Botao>
              </>
            )}
          </>
        }
      >
        {aberto && <FormEvento key={atual?.id ?? 'novo'} evento={atual} aoSalvar={() => setAberto(null)} />}
      </Drawer>
      <Confirmar aberto={!!remover} aoFechar={() => setRemover(null)} aoConfirmar={() => remover && excluir.mutate(remover.id)} titulo={t('confirmar.excluirEvento')} texto={t('confirmar.excluirEventoTexto')} perigoso carregando={excluir.isPending} />
    </>
  );
}
